import { FetchArgs } from '@reduxjs/toolkit/query';
import {
  API_LOCAL_BIOMETRICS_URL,
  API_LOCAL_CABLE_URL,
  API_LOCAL_GAMBIT_URL,
  API_LOCAL_KEYMASTER_URL,
  API_LOCAL_MOBILE_URL,
  API_LOCAL_PHOENIX_URL,
} from './constants';

export interface BackendRoute {
  pathPrefixes: string[];
  baseUrl: string;
  stripPathPrefix?: string;
}

export interface ResolvedBackendRequest {
  baseUrl: string;
  args: string | FetchArgs;
}

export const LOCAL_BACKEND_ROUTES: BackendRoute[] = [
  {
    pathPrefixes: ['/mobileapi/'],
    baseUrl: API_LOCAL_MOBILE_URL,
    stripPathPrefix: '/mobileapi',
  },
  {
    pathPrefixes: ['/gambit/'],
    baseUrl: API_LOCAL_GAMBIT_URL,
    stripPathPrefix: '/gambit',
  },
  {
    pathPrefixes: ['/keymaster/'],
    baseUrl: API_LOCAL_KEYMASTER_URL,
    stripPathPrefix: '/keymaster',
  },
  {
    pathPrefixes: ['/cable/'],
    baseUrl: API_LOCAL_CABLE_URL,
  },
  {
    // More specific than '/api/' so biometrics routes to its own service, not Phoenix.
    pathPrefixes: ['/api/biometrics/'],
    baseUrl: API_LOCAL_BIOMETRICS_URL,
    stripPathPrefix: '/api',
  },
  {
    pathPrefixes: ['/api/'],
    baseUrl: API_LOCAL_PHOENIX_URL,
  },
];

const getRequestUrl = (args: string | FetchArgs) => (typeof args === 'string' ? args : args.url);

export const findLocalBackendRoute = (requestUrl: string) =>
  [...LOCAL_BACKEND_ROUTES]
    .sort((firstRoute, secondRoute) => {
      const firstLongestPrefix = Math.max(
        ...firstRoute.pathPrefixes.map((prefix) => prefix.length),
      );
      const secondLongestPrefix = Math.max(
        ...secondRoute.pathPrefixes.map((prefix) => prefix.length),
      );

      return secondLongestPrefix - firstLongestPrefix;
    })
    .find(({ pathPrefixes }) =>
      pathPrefixes.some((pathPrefix) => requestUrl.startsWith(pathPrefix)),
    );

export const rewriteLocalBackendRequestUrl = (requestUrl: string, route: BackendRoute) => {
  if (!route.stripPathPrefix || !requestUrl.startsWith(route.stripPathPrefix)) {
    return requestUrl;
  }

  const rewrittenUrl = requestUrl.slice(route.stripPathPrefix.length);

  return rewrittenUrl.startsWith('/') ? rewrittenUrl : `/${rewrittenUrl}`;
};

export const resolveLocalBackendRequest = (
  args: string | FetchArgs,
): ResolvedBackendRequest | undefined => {
  const requestUrl = getRequestUrl(args);
  const route = findLocalBackendRoute(requestUrl);

  if (!route) {
    return undefined;
  }

  const rewrittenUrl = rewriteLocalBackendRequestUrl(requestUrl, route);

  return {
    baseUrl: route.baseUrl,
    args: typeof args === 'string' ? rewrittenUrl : { ...args, url: rewrittenUrl },
  };
};
